import { CodeProject, CodeSession, CodeArtifact } from './types'

export const PROJECTS: CodeProject[] = [
  {
    id: 'ecom',
    name: 'E-commerce site',
    targets: [
      { id: 'ecom-mobile', projectId: 'ecom', name: 'Mobile app' },
      { id: 'ecom-web', projectId: 'ecom', name: 'Web app' },
    ],
  },
  {
    id: 'internal',
    name: 'Internal tools',
    targets: [
      { id: 'internal-admin', projectId: 'internal', name: 'Admin panel' },
      { id: 'internal-billing', projectId: 'internal', name: 'Billing API' },
    ],
  },
]

export const INITIAL_SESSIONS: CodeSession[] = [
  {
    id: 's1',
    targetId: 'ecom-mobile',
    title: 'Add dark mode toggle to settings screen',
    originalRequest:
      'Add a dark mode toggle to the settings screen. It should persist across launches and respect the system preference by default.',
    createdLabel: 'this morning',
    startedAt: 'Started this morning at 9:14am',
    status: 'running',
    artifactIds: ['a1', 'a2'],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content:
          'Add a dark mode toggle to the settings screen. It should persist across launches and respect the system preference by default.',
        timestamp: '9:14am',
      },
      {
        id: 'm2',
        role: 'agent',
        content:
          "On it. Let me start by reading the current settings screen so I can see where the toggle should go.",
        timestamp: '9:14am',
        toolCalls: [
          {
            id: 't1',
            name: 'read_file',
            input: 'apps/mobile/src/screens/SettingsScreen.tsx',
            output: '287 lines read',
          },
        ],
      },
      {
        id: 'm3',
        role: 'agent',
        content:
          "Good news — there's already a Preferences section with a toggle component I can reuse. I'll add a `themeMode` preference that reads from `useColorScheme()` on first launch, then persists the user's choice in AsyncStorage.",
        timestamp: '9:15am',
        toolCalls: [
          {
            id: 't2',
            name: 'write_file',
            input: 'apps/mobile/src/theme/useThemeMode.ts',
            output: 'Created — 42 lines',
            diff: {
              file: 'apps/mobile/src/theme/useThemeMode.ts',
              added: 42,
              removed: 0,
              snippet:
                "+ export function useThemeMode() {\n+   const system = useColorScheme()\n+   const [mode, setMode] = useState<'system'|'light'|'dark'>('system')\n+   // ...persist to AsyncStorage\n+ }",
            },
          },
        ],
      },
      {
        id: 'm4',
        role: 'user',
        content:
          "Great. Also make sure the toggle label reads 'Dark mode' not 'Theme' — simpler.",
        timestamp: '9:17am',
      },
      {
        id: 'm5',
        role: 'agent',
        content:
          'Got it — renaming. One more file to touch then I\'ll run the component tests.',
        timestamp: '9:17am',
        toolCalls: [
          {
            id: 't3',
            name: 'write_file',
            input: 'apps/mobile/src/screens/SettingsScreen.tsx',
            output: 'Updated — 12 additions',
            diff: {
              file: 'apps/mobile/src/screens/SettingsScreen.tsx',
              added: 12,
              removed: 2,
            },
          },
        ],
      },
      {
        id: 'm6',
        role: 'agent',
        content: 'Running the component tests now…',
        timestamp: '9:18am',
        toolCalls: [
          {
            id: 't4',
            name: 'bash',
            input: 'pnpm test --filter settings',
            output: '14 tests passed',
          },
        ],
      },
    ],
  },
  {
    id: 's2',
    targetId: 'ecom-mobile',
    title: 'Fix cart total calculation bug',
    originalRequest:
      'The cart total is off by a cent when items have fractional tax. Track it down and fix it.',
    createdLabel: 'yesterday',
    startedAt: 'Started yesterday at 4:32pm',
    status: 'waiting',
    artifactIds: ['a3', 'a4'],
    finalAction: {
      kind: 'choice',
      question:
        "I found two ways to fix the rounding. Which approach would you like me to take?",
      choices: [
        { key: 'A', label: 'Round at the line item level (matches receipts)' },
        { key: 'B', label: 'Round at the cart total (fewer code changes)' },
        { key: 'Discuss', label: 'Discuss trade-offs first' },
      ],
    },
    messages: [
      {
        id: 'm1',
        role: 'user',
        content:
          'The cart total is off by a cent when items have fractional tax. Track it down and fix it.',
        timestamp: '4:32pm',
      },
      {
        id: 'm2',
        role: 'agent',
        content: 'Reproducing the bug with a test case first.',
        timestamp: '4:33pm',
        toolCalls: [
          {
            id: 't1',
            name: 'bash',
            input: 'pnpm test cart.total.test.ts',
            output: '1 failing — expected 21.56, received 21.57',
          },
        ],
      },
      {
        id: 'm3',
        role: 'agent',
        content:
          "Confirmed. The issue is that `computeTotal()` sums raw prices then applies tax, but the receipt renderer rounds each line item first. I can fix this two ways:",
        timestamp: '4:35pm',
      },
      {
        id: 'm4',
        role: 'agent',
        content:
          "**Option A — Round at the line item level.** Change `computeTotal()` to round each item before summing. Matches what customers see on the receipt. Touches `computeTotal`, `PriceBreakdown`, and the two tax tests.\n\n**Option B — Round at the cart total.** Leave per-item math alone and apply `Math.round(total * 100) / 100` at the end. Smaller diff, but the receipt and total can diverge by a cent on edge cases.\n\nWhich do you want me to take?",
        timestamp: '4:36pm',
      },
    ],
  },
  {
    id: 's3',
    targetId: 'ecom-mobile',
    title: 'Refactor auth screen navigation',
    originalRequest: 'Refactor the auth screens so we can deep link into any of them.',
    createdLabel: '2 days ago',
    startedAt: 'Started 2 days ago at 11:02am',
    status: 'interrupted',
    artifactIds: ['a5'],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content: 'Refactor the auth screens so we can deep link into any of them.',
        timestamp: '11:02am',
      },
      {
        id: 'm2',
        role: 'agent',
        content:
          "Starting with the navigator. I'll move the auth stack out of the tab navigator into its own root stack so deep links can target any screen directly.",
        timestamp: '11:03am',
      },
      {
        id: 'm3',
        role: 'system',
        content: 'Interrupted by Matt',
        timestamp: '11:08am',
      },
    ],
  },
  {
    id: 's4',
    targetId: 'ecom-web',
    title: 'Wire up Stripe checkout flow',
    originalRequest:
      'Integrate Stripe Checkout for the cart page. Redirect on success, show error on failure.',
    createdLabel: 'this morning',
    startedAt: 'Started this morning at 10:41am',
    status: 'running',
    artifactIds: ['a6'],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content:
          'Integrate Stripe Checkout for the cart page. Redirect on success, show error on failure.',
        timestamp: '10:41am',
      },
      {
        id: 'm2',
        role: 'agent',
        content: "Installing the Stripe SDK and scaffolding the checkout route.",
        timestamp: '10:42am',
      },
    ],
  },
  {
    id: 's5',
    targetId: 'ecom-web',
    title: 'Add product search with filters',
    originalRequest:
      'Add a search bar to the product listing page with category and price filters.',
    createdLabel: 'yesterday',
    startedAt: 'Started yesterday at 2:15pm',
    status: 'done',
    artifactIds: ['a7', 'a8'],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content: 'Add a search bar to the product listing page with category and price filters.',
        timestamp: '2:15pm',
      },
      {
        id: 'm2',
        role: 'agent',
        content: 'Done — deployed a preview URL for you to try.',
        timestamp: '3:47pm',
      },
    ],
  },
  {
    id: 's6',
    targetId: 'internal-admin',
    title: 'Fix timezone bug in invoice export',
    originalRequest:
      'Invoices exported to CSV are showing dates one day early for UTC-negative timezones.',
    createdLabel: 'this morning',
    startedAt: 'Started this morning at 8:20am',
    status: 'waiting',
    artifactIds: ['a9'],
    finalAction: {
      kind: 'open',
      question:
        "I can either force UTC in the export or use the tenant's configured timezone. What would you prefer? You can reply in words.",
    },
    messages: [
      {
        id: 'm1',
        role: 'user',
        content: 'Invoices exported to CSV are showing dates one day early for UTC-negative timezones.',
        timestamp: '8:20am',
      },
      {
        id: 'm2',
        role: 'agent',
        content:
          "I can either force UTC in the export or use the tenant's configured timezone. What would you prefer? You can reply in words.",
        timestamp: '8:22am',
      },
    ],
  },
  {
    id: 's7',
    targetId: 'internal-billing',
    title: 'Add webhook retry logic',
    originalRequest:
      'When a downstream webhook fails, retry up to 5 times with exponential backoff and log each attempt.',
    createdLabel: 'yesterday',
    startedAt: 'Started yesterday at 5:02pm',
    status: 'running',
    artifactIds: ['a10'],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content:
          'When a downstream webhook fails, retry up to 5 times with exponential backoff and log each attempt.',
        timestamp: '5:02pm',
      },
      {
        id: 'm2',
        role: 'agent',
        content: "Looking at the existing webhook dispatcher. I'll add a retry queue using BullMQ.",
        timestamp: '5:04pm',
      },
    ],
  },
  {
    id: 's8',
    targetId: 'internal-billing',
    title: 'Migrate logging to structured JSON',
    originalRequest: 'Replace the string-based logs with structured pino logs.',
    createdLabel: 'last week',
    startedAt: 'Started last week',
    status: 'done',
    artifactIds: [],
    messages: [
      {
        id: 'm1',
        role: 'user',
        content: 'Replace the string-based logs with structured pino logs.',
        timestamp: '',
      },
      {
        id: 'm2',
        role: 'agent',
        content: 'Done. Swept every call site and added pino plus a redaction policy.',
        timestamp: '',
      },
    ],
  },
]

export const INITIAL_ARTIFACTS: CodeArtifact[] = [
  {
    id: 'a1',
    sessionId: 's1',
    kind: 'pages-updated',
    title: 'Dark mode toggle — 2 files changed',
    timestamp: '9:17am',
    status: 'resolved',
    files: [
      {
        path: 'apps/mobile/src/theme/useThemeMode.ts',
        added: 42,
        removed: 0,
        snippet:
          "+ export function useThemeMode() {\n+   const system = useColorScheme()\n+   const [mode, setMode] = useState<'system'|'light'|'dark'>('system')\n+   useEffect(() => { AsyncStorage.getItem('themeMode').then(v => v && setMode(v as any)) }, [])\n+   return { mode, setMode: (m: any) => { setMode(m); AsyncStorage.setItem('themeMode', m) } }\n+ }",
      },
      {
        path: 'apps/mobile/src/screens/SettingsScreen.tsx',
        added: 12,
        removed: 2,
        snippet:
          "- <Toggle label=\"Theme\" value={isDark} onChange={setDark} />\n+ <Toggle label=\"Dark mode\" value={mode === 'dark'} onChange={v => setMode(v ? 'dark' : 'light')} />",
      },
    ],
  },
  {
    id: 'a2',
    sessionId: 's1',
    kind: 'test-results',
    title: 'Component tests — settings',
    timestamp: '9:18am',
    status: 'resolved',
    passed: 14,
    failed: 0,
    failingTests: [],
  },
  {
    id: 'a3',
    sessionId: 's2',
    kind: 'hitl-step',
    title: 'Which rounding approach?',
    timestamp: '4:36pm',
    status: 'pending',
    question:
      "I found two ways to fix the rounding. Which approach would you like me to take?",
    mode: 'choice',
    choices: [
      { key: 'A', label: 'Round at the line item level (matches receipts)' },
      { key: 'B', label: 'Round at the cart total (fewer code changes)' },
      { key: 'Discuss', label: 'Discuss trade-offs first' },
    ],
  },
  {
    id: 'a4',
    sessionId: 's2',
    kind: 'test-results',
    title: 'Cart total — reproducer',
    timestamp: '4:34pm',
    status: 'pending',
    passed: 12,
    failed: 1,
    failingTests: [
      {
        name: 'cart.total · fractional tax',
        error: 'expected 21.56, received 21.57',
      },
    ],
  },
  {
    id: 'a5',
    sessionId: 's3',
    kind: 'pages-updated',
    title: 'Auth navigator refactor — in progress',
    timestamp: '11:07am',
    status: 'resolved',
    files: [
      {
        path: 'apps/mobile/src/navigation/AuthStack.tsx',
        added: 68,
        removed: 4,
      },
      {
        path: 'apps/mobile/src/navigation/RootNavigator.tsx',
        added: 9,
        removed: 21,
      },
    ],
  },
  {
    id: 'a6',
    sessionId: 's4',
    kind: 'pages-updated',
    title: 'Stripe checkout scaffold',
    timestamp: '10:52am',
    status: 'resolved',
    files: [
      { path: 'apps/web/src/pages/checkout.tsx', added: 84, removed: 0 },
      { path: 'apps/web/src/lib/stripe.ts', added: 26, removed: 0 },
    ],
  },
  {
    id: 'a7',
    sessionId: 's5',
    kind: 'preview-url',
    title: 'Product search — live preview',
    timestamp: '3:47pm',
    status: 'resolved',
    url: 'https://ecom-web-git-product-search.vercel.app',
    description: 'Deployed from branch product-search · commit a1b2c3d',
  },
  {
    id: 'a8',
    sessionId: 's5',
    kind: 'test-results',
    title: 'E2E tests — search flow',
    timestamp: '3:41pm',
    status: 'resolved',
    passed: 23,
    failed: 0,
    failingTests: [],
  },
  {
    id: 'a9',
    sessionId: 's6',
    kind: 'hitl-step',
    title: 'UTC or tenant timezone?',
    timestamp: '8:22am',
    status: 'pending',
    question:
      "I can either force UTC in the export or use the tenant's configured timezone. What would you prefer? You can reply in words.",
    mode: 'open',
  },
  {
    id: 'a10',
    sessionId: 's7',
    kind: 'preview-url',
    title: 'Billing API staging',
    timestamp: '5:48pm',
    status: 'resolved',
    url: 'https://billing-api-staging.internal',
    description: 'Retry logic deployed to staging · branch webhook-retries',
  },
]
